import { useState, useCallback, useRef } from 'react'
import { generatePropReference } from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 15000

const getCreativeGuidelineSessionId = source =>
  source?.sessionId ||
  source?.session_id ||
  source?.creative_guideline_session_id ||
  source?.creativeGuidelineSessionId ||
  source?.creativeDirection?.sessionId ||
  source?.creativeDirection?.session_id ||
  source?.creativeDirection?.creative_guideline_session_id ||
  source?.creativeDirection?.creativeGuidelineSessionId ||
  null

const getVideoCreationMode = formData =>
  formData?.aiVideoFlowMode ||
  formData?.video_creation_mode ||
  formData?.videoCreationMode ||
  formData?.creativeDirection?.flowMode ||
  formData?.creativeDirection?.flow_mode ||
  null

const getCreativeGuidelinePayload = formData => {
  const sessionId = getCreativeGuidelineSessionId(formData)

  return getVideoCreationMode(formData) === 'advanced' && sessionId
    ? { creative_guideline_session: sessionId }
    : {}
}

const isRequestCancelled = error =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.message === 'canceled'

const getTaskIdFromResponse = response =>
  response?.data?.task_id || response?.data?.taskId || null

const getErrorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Prop reference generation failed'

/**
 * Custom hook to manage prop reference generation and polling
 * Handles prop map generation, editing, and batch updates
 */
export const usePropReferences = ({ formData, setFormData, videoId } = {}) => {
  const {
    isPolling,
    progress: propGenerationProgress,
    status: propGenerationStatus,
    activeTaskId,
    startPolling,
    stopPolling
  } = useTaskPoller({ pollInterval: POLL_INTERVAL })

  const [isRequesting, setIsRequesting] = useState(false)
  const requestAbortRef = useRef(null)
  const activeTaskIdRef = useRef(null)
  const stopRequestedRef = useRef(false)
  const generationTokenRef = useRef(0)

  const updatePropGenerationState = useCallback(
    updates => {
      setFormData(prev => {
        const referenceGeneration = prev.referenceGeneration || {}
        const previousState = referenceGeneration.props || {}
        const hasTaskIdUpdate = Object.prototype.hasOwnProperty.call(
          updates,
          'taskId'
        )
        const taskId = hasTaskIdUpdate
          ? updates.taskId
          : previousState.taskId || activeTaskIdRef.current || null

        return {
          ...prev,
          referenceGeneration: {
            ...referenceGeneration,
            props: {
              ...previousState,
              ...updates,
              taskId,
              updatedAt: new Date().toISOString()
            }
          }
        }
      })
    },
    [setFormData]
  )

  const runPropReferenceTask = useCallback(
    async (requestData, onSuccess, errorLabel) => {
      const runToken = generationTokenRef.current + 1
      generationTokenRef.current = runToken
      stopRequestedRef.current = false
      activeTaskIdRef.current = null

      if (requestAbortRef.current) {
        requestAbortRef.current.abort()
      }

      const abortController = new AbortController()
      requestAbortRef.current = abortController
      setIsRequesting(true)
      updatePropGenerationState({
        status: 'requesting',
        taskId: null,
        progress: 0,
        error: null
      })

      const isCurrentRun = () =>
        generationTokenRef.current === runToken && !stopRequestedRef.current

      try {
        const propRes = await generatePropReference(requestData, {
          signal: abortController.signal
        })

        if (!isCurrentRun()) return null

        const taskId = getTaskIdFromResponse(propRes)
        if (!taskId) {
          throw new Error('No task ID returned from prop generation')
        }

        activeTaskIdRef.current = taskId
        setIsRequesting(false)
        updatePropGenerationState({
          status: 'processing',
          taskId,
          progress: 0,
          error: null
        })

        const result = await startPolling(taskId)
        if (!isCurrentRun()) return null

        if (result?.status === 'cancelled') {
          updatePropGenerationState({
            status: 'stopped',
            taskId,
            progress: propGenerationProgress || 0
          })
          return null
        }

        if (result && result.status === 'success' && result.output) {
          onSuccess(result.output)
          updatePropGenerationState({
            status: 'success',
            taskId,
            progress: 100,
            error: null
          })
        }

        return result
      } catch (error) {
        if (
          isRequestCancelled(error) ||
          stopRequestedRef.current ||
          generationTokenRef.current !== runToken
        ) {
          updatePropGenerationState({
            status: 'stopped',
            taskId: activeTaskIdRef.current,
            progress: propGenerationProgress || 0
          })
          return null
        }

        console.error(errorLabel, error)
        updatePropGenerationState({
          status: 'failed',
          taskId: activeTaskIdRef.current,
          error: getErrorMessage(error)
        })
        throw error
      } finally {
        if (generationTokenRef.current === runToken) {
          requestAbortRef.current = null
          setIsRequesting(false)
        }
      }
    },
    [
      propGenerationProgress,
      setFormData,
      startPolling,
      updatePropGenerationState
    ]
  )

  const stopPropReferenceGeneration = useCallback(() => {
    stopRequestedRef.current = true
    generationTokenRef.current += 1

    if (requestAbortRef.current) {
      requestAbortRef.current.abort()
      requestAbortRef.current = null
    }

    stopPolling()
    setIsRequesting(false)
    updatePropGenerationState({
      status: 'stopped',
      taskId: activeTaskIdRef.current || activeTaskId || null,
      progress: propGenerationProgress || 0
    })
  }, [
    activeTaskId,
    propGenerationProgress,
    stopPolling,
    updatePropGenerationState
  ])

  /**
   * Generate prop map references
   */
  const generatePropMap = useCallback(
    async (propMapObj, existingUris) => {
      return runPropReferenceTask(
        {
          prop_map_with_prompts: propMapObj,
          existing_prop_uris:
            existingUris !== undefined
              ? existingUris
              : formData?.props_reference,
          video_id: videoId,
          ...getCreativeGuidelinePayload(formData)
        },
        output => {
          if (output) {
            setFormData(prev => ({
              ...prev,
              props_reference: output
            }))
          }
        },
        'Error generating prop references:'
      )
    },
    [
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.props_reference,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runPropReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Add new prop reference
   */
  const handleAddPropReference = useCallback(
    async newPropMap => {
      try {
        return runPropReferenceTask(
          {
            prop_map_with_prompts: {
              ...(formData?.propMap || {}),
              ...newPropMap
            },
            existing_prop_uris: formData?.props_reference,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...newPropMap
              }
            }))
          },
          'Error adding prop reference:'
        )
      } catch (error) {
        console.error('Error adding prop reference:', error)
      }
    },
    [
      formData?.propMap,
      formData?.props_reference,
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runPropReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Edit existing prop reference
   */
  const handleEditPropReference = useCallback(
    async editingPropPrompt => {
      try {
        const editingKeys = Object.keys(editingPropPrompt)

        // Remove old URIs for edited props
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!editingKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        return runPropReferenceTask(
          {
            prop_map_with_prompts: {
              ...(formData?.propMap || {}),
              ...editingPropPrompt
            },
            existing_prop_uris: filteredPropUris,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...editingPropPrompt
              }
            }))
          },
          'Error editing prop reference:'
        )
      } catch (error) {
        console.error('Error editing prop reference:', error)
      }
    },
    [
      formData?.propMap,
      formData?.props_reference,
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runPropReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Batch update multiple props
   */
  const handleBatchUpdateProps = useCallback(
    async (updatedPropMap, modifiedKeys) => {
      try {
        // Filter out URIs for modified keys
        const filteredPropUris = Object.keys(
          formData?.props_reference || {}
        ).reduce((acc, key) => {
          if (!modifiedKeys.includes(key)) {
            acc[key] = formData.props_reference[key]
          }
          return acc
        }, {})

        return runPropReferenceTask(
          {
            prop_map_with_prompts: updatedPropMap,
            existing_prop_uris: filteredPropUris,
            video_id: videoId,
            ...getCreativeGuidelinePayload(formData)
          },
          output => {
            if (!output) return
            setFormData(prev => ({
              ...prev,
              props_reference: {
                ...(prev.props_reference || {}),
                ...output
              },
              propMap: {
                ...(prev.propMap || {}),
                ...updatedPropMap
              }
            }))
          },
          'Error batch updating prop references:'
        )
      } catch (error) {
        console.error('Error batch updating prop references:', error)
      }
    },
    [
      formData?.aiVideoFlowMode,
      formData?.creativeDirection,
      formData?.props_reference,
      formData?.videoCreationMode,
      formData?.video_creation_mode,
      runPropReferenceTask,
      setFormData,
      videoId
    ]
  )

  /**
   * Reset prop generation state
   */
  const resetPropGeneration = useCallback(() => {
    // Note: useTaskPoller handles its own state, but we provide this for compatibility
  }, [])

  return {
    // State
    isGeneratingPropReference: isPolling || isRequesting,
    propGenerationProgress,
    propGenerationStatus,

    // Actions
    generatePropMap,
    handleAddPropReference,
    handleEditPropReference,
    handleBatchUpdateProps,
    resetPropGeneration,
    stopPropReferenceGeneration
  }
}

export default usePropReferences
